// REACT & REACT HOOKS
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";

// STORE ACTIONS
import { registerUser, getInvitation } from "store/actions";

// COMPONENTS
import Loader from "components/shared/loader";

// STYLES & ASSETS
import styles from "./styles/style.module.scss";
import logo from "./assets/images/full-logo.png";

// UI COMPONENTS
import { Container, Form } from "react-bootstrap";

// ICONS
import { ReactComponent as ProfileIcon } from "./assets/svg/profile.svg";
import { ReactComponent as EmailIcon } from "./assets/svg/email-2.svg";
import { ReactComponent as PhoneIcon } from "./assets/svg/phone.svg";
import { ReactComponent as PinIcon } from "./assets/svg/pin.svg";
import { ReactComponent as PassIcon } from "./assets/svg/password.svg";
import { ReactComponent as TogglePassIcon } from "./assets/svg/view-toggle.svg";

const Register = () => {
    const dispatch = useDispatch();
    const { loading, error, user } = useSelector(
        (state) => state.authentication
    );
    const navigate = useNavigate();
    const { formatMessage } = useIntl();
    const [showPassword, setShowPassword] = useState(false);
    const [searchParams] = useSearchParams();
    const { register, handleSubmit, reset } = useForm();

    const searchParamsObj = Object.fromEntries([...searchParams]);

    useEffect(() => {
        if (user?.email)
            reset({
                ...user,
                firstName: user?.firstName,
                lastName: user?.lastName,
                email: user?.email,
            });
        if (searchParamsObj?.code?.length > 0 && !user?.email)
            dispatch(getInvitation(searchParamsObj?.code));
    }, [reset, searchParams, user]);

    useEffect(() => {
        if (error) {
            toast(error, { toastId: error });
        }
    }, [error]);

    const handleShowPassword = (event) => {
        event.preventDefault();

        setShowPassword((prevShowPassword) => {
            return !prevShowPassword;
        });
    };

    const onSubmit = (data) => {
        dispatch(
            registerUser({
                data,
                navigate,
                searchParams: searchParamsObj?.code
                    ? `code=${searchParamsObj?.code}`
                    : "",
            })
        );
    };

    if (loading) return <Loader />;
    return (
        <div className={styles["register-page"]}>
            <div className="register-wrapper">
                <Container>
                    <div className="logo">
                        <img src={logo} alt="" />
                    </div>
                    <div className="register-data">
                        <h3>أهلا بك</h3>
                        <p>تسجيل حساب جديد</p>
                    </div>

                    <form
                        className="register-form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <div className="name">
                            <Form.Group className="input-container">
                                <ProfileIcon width="24" height="20" />

                                <Form.Control
                                    {...register("firstName")}
                                    placeholder={formatMessage({
                                        id: "firstName",
                                    })}
                                />
                            </Form.Group>
                            <Form.Group className="input-container">
                                <ProfileIcon width="24" height="20" />

                                <Form.Control
                                    {...register("lastName")}
                                    placeholder={formatMessage({
                                        id: "lastName",
                                    })}
                                />
                            </Form.Group>
                        </div>

                        <Form.Group className="input-container">
                            <EmailIcon
                                className="email-icon"
                                width="24"
                                height="20"
                            />

                            <Form.Control
                                type="email"
                                {...register("email")}
                                placeholder={formatMessage({ id: "email" })}
                                {...(user?.email ? { readOnly: true } : {})}
                            />
                        </Form.Group>

                        <Form.Group className="input-container">
                            <PhoneIcon width="24" height="20" />

                            <Form.Control
                                type="number"
                                {...register("phone")}
                                placeholder={formatMessage({ id: "phone" })}
                            />
                        </Form.Group>

                        <Form.Group className="input-container">
                            <PinIcon width="24" height="20" />

                            <Form.Control
                                {...register("address")}
                                placeholder={formatMessage({ id: "address" })}
                            />
                        </Form.Group>

                        <Form.Group className="input-container">
                            <PassIcon
                                className="password-icon"
                                width="24"
                                height="20"
                            />

                            <Form.Control
                                className="password"
                                type={showPassword ? "text" : "password"}
                                {...register("password")}
                                placeholder={formatMessage({ id: "password" })}
                            />

                            <TogglePassIcon
                                onClick={handleShowPassword}
                                className={`password-toggler ${
                                    showPassword ? "active" : ""
                                }`}
                            />
                        </Form.Group>

                        <Form.Group className="input-container">
                            <button className="btn">
                                <FormattedMessage id="register" />
                            </button>
                        </Form.Group>
                    </form>
                </Container>
            </div>
        </div>
    );
};

export default Register;
